import { useState, useCallback } from 'react'
import { useAuth } from 'auth/useAuth'
import { setupApiAuth } from '../../helpers/auth-helper'
import { STATUS } from '../constants'

/**
 * Turns axios/HTTP failures into a short pt-BR message the user can act on.
 * The raw error is still logged for debugging.
 */
export const friendlyApiError = (err) => {
  const status = err?.response?.status
  const serverMsg = err?.response?.data?.message
  if (!err?.response) {
    return err?.code === 'ECONNABORTED'
      ? 'A conexão demorou demais. Tente novamente.'
      : 'Sem conexão com o servidor. Verifique sua internet e tente novamente.'
  }
  switch (status) {
    case 400: return serverMsg || 'Dados inválidos. Verifique os campos e tente novamente.'
    case 401: return 'Sua sessão expirou. Entre novamente.'
    case 403: return 'Você não tem permissão para esta ação.'
    case 404: return 'Registro não encontrado.'
    case 409: return serverMsg || 'Já existe um registro com esses dados.'
    case 429: return 'Muitas solicitações em pouco tempo. Aguarde um instante.'
    default:
      return status >= 500
        ? 'O servidor não conseguiu concluir a operação. Tente novamente em instantes.'
        : (serverMsg || 'Não foi possível concluir a operação. Tente novamente.')
  }
}

export const useApi = () => {
  const auth = useAuth()
  const [status, setStatus] = useState(STATUS.IDLE)
  const [error, setError] = useState(null)

  const execute = useCallback(async (apiCall, options = {}) => {
    const { onSuccess, onError } = options

    if (!auth.isAuthenticated) {
      const authError = 'Você precisa estar conectado para continuar.'
      setError(authError)
      onError?.(authError)
      return { success: false, error: authError }
    }

    setStatus(STATUS.LOADING)
    setError(null)

    try {
      setupApiAuth(auth)
      const result = await apiCall()
      setStatus(STATUS.SUCCESS)
      onSuccess?.(result)
      return { success: true, data: result }
    } catch (err) {
      // 404 on profile/curriculum is normal for new users — treat as empty.
      if (err.response?.status === 404) {
        setStatus(STATUS.SUCCESS)
        onSuccess?.(null)
        return { success: true, data: null }
      }

      console.error('API call failed:', err)
      const errorMessage = friendlyApiError(err)
      setStatus(STATUS.ERROR)
      setError(errorMessage)
      onError?.(errorMessage)
      return { success: false, error: errorMessage }
    }
  }, [auth])

  return {
    status,
    error,
    isLoading: status === STATUS.LOADING,
    isSuccess: status === STATUS.SUCCESS,
    execute
  }
}
